import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, faPlus, faTimes, faRefresh, faDollarSign, 
    faShoppingCart, faTag, faCalendar, faCube,
    faList, faTh, faTrash, faExclamationTriangle, faChevronDown, faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { LoadingSpinner, ErrorMessage, EmptyState } from './CommonUI';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import AdSenseAd from './AdSenseAd';

const PriceTool = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedSets, setSelectedSets] = useState(new Set());
    const [priceList, setPriceList] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingPrices, setIsLoadingPrices] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [priceError, setPriceError] = useState(null);
    const [loadedImages, setLoadedImages] = useState({});
    const [activeView, setActiveView] = useState('grid');
    const [showOnlyWithPrices, setShowOnlyWithPrices] = useState(false);
    const [searchResultsVisible, setSearchResultsVisible] = useState(true);

    useDocumentTitle('Price tool');

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        
        setIsSearching(true);
        setSearchError(null);
        setSearchResultsVisible(true);
        
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/search_sets_with_prices.php?query=${encodeURIComponent(searchQuery.trim())}`
            );
            
            if (response.data.error) {
                setSearchError(response.data.message);
                setSearchResults([]);
            } else {
                setSearchResults(response.data);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchError('Failed to search sets. Please try again.');
            setSearchResults([]);
        }
        
        setIsSearching(false);
    };

    const clearSearchResults = () => {
        setSearchResults([]);
        setSearchError(null);
        setSearchQuery('');
    };

    const addToPriceList = (set) => {
        const setNum = set.set_num;
        if (selectedSets.has(setNum)) return;
        setSelectedSets(prev => new Set([...prev, setNum]));
        setPriceList(prev => [...prev, set]);
    };

    const removeFromPriceList = (setNum) => {
        setSelectedSets(prev => {
            const newSet = new Set(prev);
            newSet.delete(setNum);
            return newSet;
        });
        setPriceList(prev => prev.filter(set => set.set_num !== setNum));
    };

    const refreshPriceData = async () => {
        if (priceList.length === 0) return;
        
        setIsLoadingPrices(true);
        setPriceError(null);
        
        try {
            const setNums = priceList.map(set => set.set_num).join(',');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/get_price_data.php?set_nums=${setNums}`
            );
            
            if (response.data.error) {
                setPriceError(response.data.error);
            } else {
                setPriceList(response.data.sets);
            }
        } catch (error) {
            console.error('Price refresh error:', error);
            setPriceError('Failed to refresh price data. Please try again.');
        }
        
        setIsLoadingPrices(false);
    };

    const clearPriceList = () => {
        setSelectedSets(new Set());
        setPriceList([]);
    };

    const handleImageError = (e) => {
        e.target.src = '/images/lego_piece_questionmark.png';
    };

    const handleImageLoad = (setNum) => {
        setLoadedImages(prev => ({ ...prev, [setNum]: true }));
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const getFilteredPriceList = () => {
        if (!showOnlyWithPrices) return priceList;
        return priceList.filter(set => set.prices.has_price_data);
    };

    const calculateTotals = () => {
        const filteredList = getFilteredPriceList();
        return filteredList.reduce((totals, set) => {
            const retail = set.prices.retail_price ? parseFloat(set.prices.retail_price) : 0;
            const sealed = set.prices.sealed_value ? parseFloat(set.prices.sealed_value) : retail;
            const used   = set.prices.used_value   ? parseFloat(set.prices.used_value)   : retail;

            totals.retail += retail;
            if (set.prices.market_price) totals.market += parseFloat(set.prices.market_price);
            totals.sealed += sealed;
            totals.used   += used;

            // Track how many sets are using retail as a fallback for sealed/used
            if (!set.prices.sealed_value && retail > 0) totals.sealedFallbackCount++;
            if (!set.prices.used_value   && retail > 0) totals.usedFallbackCount++;

            return totals;
        }, { retail: 0, market: 0, sealed: 0, used: 0, sealedFallbackCount: 0, usedFallbackCount: 0 });
    };

    const renderSearchSection = () => (
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 md:p-6">
                <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search sets by name or number..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base bg-gray-50 focus:bg-white transition-colors"
                        />
                    </div>
                    <button 
                        type="submit"
                        disabled={isSearching || !searchQuery.trim()}
                        className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium whitespace-nowrap"
                    >
                        {isSearching ? (
                            <div className="w-4 h-4 simple-spinner" style={{ borderWidth: '2px', borderColor: 'white', borderTopColor: 'transparent' }}></div>
                        ) : (
                            <FontAwesomeIcon icon={faSearch} />
                        )}
                        <span className="hidden sm:inline">Search</span>
                    </button>
                </form>
            </div>
            
            {searchError && (
                <div className="px-4 pb-4 md:px-6">
                    <ErrorMessage message={searchError} />
                </div>
            )}
            
            {searchResults.length > 0 && (
                <div className="border-t border-gray-100">
                    <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gray-50 border-b border-gray-100">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-700">
                                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                            </span>
                            <button
                                onClick={() => setSearchResultsVisible(v => !v)}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                                title={searchResultsVisible ? 'Collapse results' : 'Expand results'}
                            >
                                <FontAwesomeIcon icon={searchResultsVisible ? faChevronUp : faChevronDown} className="text-xs" />
                            </button>
                        </div>
                        <button
                            onClick={clearSearchResults}
                            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-red-50"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>Clear results</span>
                        </button>
                    </div>

                    {searchResultsVisible && (
                        <div className="p-4 md:p-6">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                                {searchResults.map(set => (
                                    <div
                                        key={set.set_num}
                                        className={`bg-white rounded-lg border transition-all duration-200 hover:shadow-md ${
                                            selectedSets.has(set.set_num) 
                                                ? 'ring-2 ring-blue-500 border-blue-200 bg-blue-50/30' 
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <div className="p-3">
                                            <div className="relative h-24 flex items-center justify-center mb-2">
                                                {!loadedImages[set.set_num] && (
                                                    <Skeleton height="100%" width="100%" />
                                                )}
                                                <img
                                                    src={set.img_url}
                                                    alt={set.name}
                                                    className={`h-full object-contain ${loadedImages[set.set_num] ? 'opacity-100' : 'opacity-0'}`}
                                                    onError={handleImageError}
                                                    onLoad={() => handleImageLoad(set.set_num)}
                                                    style={{ transition: 'opacity 0.3s' }}
                                                />
                                            </div>
                                            
                                            <p className="font-medium text-gray-800 text-xs leading-snug line-clamp-2 mb-2 h-8">
                                                {set.name}
                                            </p>
                                            
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-xs text-gray-500 font-mono">{set.set_num}</span>
                                                <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{set.year}</span>
                                            </div>
                                            
                                            <p className="text-xs text-gray-400 truncate mb-2">{set.theme_name}</p>
                                            
                                            <div className="grid grid-cols-2 gap-1 mb-3 text-xs">
                                                <div className="bg-gray-50 rounded px-1.5 py-1 flex flex-col">
                                                    <span className="text-gray-400 text-[10px]">Retail</span>
                                                    <span className={set.prices.retail_price ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                                                        {formatPrice(set.prices.retail_price)}
                                                    </span>
                                                </div>
                                                <div className="bg-gray-50 rounded px-1.5 py-1 flex flex-col">
                                                    <span className="text-gray-400 text-[10px]">Sealed</span>
                                                    <span className={set.prices.sealed_value ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                                                        {formatPrice(set.prices.sealed_value)}
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <button
                                                onClick={() => selectedSets.has(set.set_num) 
                                                    ? removeFromPriceList(set.set_num)
                                                    : addToPriceList(set)
                                                }
                                                className={`w-full py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                                                    selectedSets.has(set.set_num)
                                                        ? 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'
                                                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                                                }`}
                                            >
                                                <FontAwesomeIcon icon={selectedSets.has(set.set_num) ? faTimes : faPlus} />
                                                <span className="whitespace-nowrap">
                                                    {selectedSets.has(set.set_num) ? 'Remove' : 'Add to List'}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderPriceListHeader = () => {
        if (priceList.length === 0) return null;
        
        const totals = calculateTotals();
        const filteredList = getFilteredPriceList();
        
        return (
            <div className="p-4 md:p-5 border-b border-gray-200 bg-gray-50/50">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faDollarSign} className="text-green-600" />
                        Price List
                        <span className="text-sm font-normal text-gray-500">({filteredList.length} set{filteredList.length !== 1 ? 's' : ''})</span>
                    </h2>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={refreshPriceData}
                            disabled={isLoadingPrices}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs font-medium disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faRefresh} className={isLoadingPrices ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                        <button
                            onClick={clearPriceList}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-red-50 text-red-600 border border-red-200 hover:border-red-300 rounded-lg transition-colors text-xs font-medium whitespace-nowrap"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                            Clear All
                        </button>
                    </div>
                </div>

                {filteredList.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                        {[
                            { label: 'Retail Total', value: totals.retail, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-100', fallback: 0 },
                            { label: 'Sealed Total', value: totals.sealed, color: 'text-green-700', bg: 'bg-green-50 border-green-100', fallback: totals.sealedFallbackCount },
                            ...(totals.market > 0 ? [{ label: 'Market Total', value: totals.market, color: 'text-purple-700', bg: 'bg-purple-50 border-purple-100', fallback: 0 }] : []),
                            { label: 'Used Total', value: totals.used, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-100', fallback: totals.usedFallbackCount },
                        ].map(stat => (
                            <div key={stat.label} className={`${stat.bg} border rounded-lg px-3 py-2`}>
                                <p className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                                    {stat.label}
                                    {stat.fallback > 0 && (
                                        <span className="text-[10px] text-gray-400 font-normal" title={`${stat.fallback} set${stat.fallback !== 1 ? 's' : ''} using retail price as estimate`}>
                                            (est.)
                                        </span>
                                    )}
                                </p>
                                <p className={`text-sm font-bold ${stat.color}`}>${stat.value.toFixed(2)}</p>
                            </div>
                        ))}
                    </div>
                )}
                
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors">
                        <input
                            type="checkbox"
                            checked={showOnlyWithPrices}
                            onChange={(e) => setShowOnlyWithPrices(e.target.checked)}
                            className="h-3.5 w-3.5 accent-blue-600"
                        />
                        Only show sets with prices
                    </label>
                    
                    <div className="flex border border-gray-200 rounded-lg overflow-hidden bg-white text-xs ml-auto">
                        <button
                            onClick={() => setActiveView('grid')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 whitespace-nowrap transition-colors ${
                                activeView === 'grid' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <FontAwesomeIcon icon={faTh} />
                            Grid
                        </button>
                        <button
                            onClick={() => setActiveView('list')}
                            className={`flex items-center gap-1.5 px-3 py-1.5 whitespace-nowrap transition-colors border-l border-gray-200 ${
                                activeView === 'list' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            <FontAwesomeIcon icon={faList} />
                            List
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderSetGridView = (set) => (
        <div
            key={set.set_num}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300 group"
        >
            <div className="p-3 md:p-4">
                <div className="relative h-32 md:h-36 flex items-center justify-center mb-3 bg-gray-50 rounded-lg overflow-hidden">
                    <img
                        src={set.img_url}
                        alt={set.name}
                        className="h-full object-contain p-2 transition-transform duration-200 group-hover:scale-105"
                        onError={handleImageError}
                    />
                    <button
                        onClick={() => removeFromPriceList(set.set_num)}
                        className="absolute top-1.5 right-1.5 w-6 h-6 bg-white hover:bg-red-600 text-gray-400 hover:text-white border border-gray-200 hover:border-red-600 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm"
                        title="Remove from list"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                </div>
                
                <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm leading-snug" style={{ minHeight: '2.5rem' }}>
                    {set.name}
                </h3>
                
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 font-mono">{set.set_num}</span>
                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{set.year}</span>
                </div>
                
                <p className="text-xs text-gray-400 mb-3 truncate">
                    {set.theme_name} &middot; {parseInt(set.num_parts).toLocaleString()} pcs
                </p>
                
                {set.prices.has_price_data ? (
                    <div className="space-y-1.5 border-t border-gray-100 pt-3">
                        {[
                            { label: 'Retail', value: set.prices.retail_price, icon: faTag, color: 'text-blue-600' },
                            { label: 'Sealed', value: set.prices.sealed_value, icon: faShoppingCart, color: 'text-green-600' },
                            ...(set.prices.market_price ? [{ label: 'Market', value: set.prices.market_price, icon: faDollarSign, color: 'text-purple-600' }] : []),
                            { label: 'Used', value: set.prices.used_value, icon: faCube, color: 'text-amber-600' },
                        ].map(row => (
                            <div key={row.label} className="flex items-center justify-between text-xs">
                                <span className={`flex items-center gap-1.5 text-gray-500`}>
                                    <FontAwesomeIcon icon={row.icon} className={row.color} />
                                    {row.label}
                                </span>
                                <span className={row.value ? 'font-semibold text-gray-800' : 'text-gray-400'}>
                                    {formatPrice(row.value)}
                                </span>
                            </div>
                        ))}
                        {set.prices.updated_at && (
                            <p className="text-[10px] text-gray-400 flex items-center gap-1 pt-1">
                                <FontAwesomeIcon icon={faCalendar} />
                                Updated {new Date(set.prices.updated_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="border-t border-gray-100 pt-3">
                        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center gap-2">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                            <span>No price data available</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderSetListView = (set) => (
        <div
            key={set.set_num}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-gray-300"
        >
            <div className="flex flex-col sm:flex-row">
                <div className="sm:w-32 md:w-40 p-3 flex items-center justify-center bg-gray-50 relative flex-shrink-0">
                    <img
                        src={set.img_url}
                        alt={set.name}
                        className="h-20 md:h-24 object-contain"
                        onError={handleImageError}
                    />
                    <button
                        onClick={() => removeFromPriceList(set.set_num)}
                        className="absolute top-1.5 right-1.5 w-5 h-5 bg-white hover:bg-red-600 text-gray-400 hover:text-white border border-gray-200 hover:border-red-600 rounded-full flex items-center justify-center transition-all duration-150 shadow-sm"
                        title="Remove from list"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                    </button>
                </div>
                
                <div className="flex-1 p-3 md:p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 text-sm leading-snug mb-1.5">
                            {set.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-1.5 mb-1">
                            <span className="text-xs text-gray-500 font-mono bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">{set.set_num}</span>
                            <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">{set.year}</span>
                            <span className="text-xs text-gray-400">{set.theme_name}</span>
                        </div>
                        <p className="text-xs text-gray-400">{parseInt(set.num_parts).toLocaleString()} pieces</p>
                        {set.prices.updated_at && (
                            <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-1">
                                <FontAwesomeIcon icon={faCalendar} />
                                Updated {new Date(set.prices.updated_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                    
                    <div className="sm:w-64 md:w-72 flex-shrink-0">
                        {set.prices.has_price_data ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                {[
                                    { label: 'Retail', value: set.prices.retail_price, icon: faTag, color: 'text-blue-600', bg: 'bg-blue-50' },
                                    { label: 'Sealed', value: set.prices.sealed_value, icon: faShoppingCart, color: 'text-green-600', bg: 'bg-green-50' },
                                    ...(set.prices.market_price ? [{ label: 'Market', value: set.prices.market_price, icon: faDollarSign, color: 'text-purple-600', bg: 'bg-purple-50' }] : []),
                                    { label: 'Used', value: set.prices.used_value, icon: faCube, color: 'text-amber-600', bg: 'bg-amber-50' },
                                ].map(col => (
                                    <div key={col.label} className={`${col.bg} rounded-lg p-2 text-center`}>
                                        <p className="text-[10px] text-gray-500 mb-0.5 flex items-center justify-center gap-1">
                                            <FontAwesomeIcon icon={col.icon} className={`${col.color} text-[9px]`} />
                                            {col.label}
                                        </p>
                                        <p className={`text-xs font-bold ${col.value ? 'text-gray-800' : 'text-gray-400'}`}>
                                            {formatPrice(col.value)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center gap-2">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                                <span>No price data available</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPriceListSection = () => {
        if (priceList.length === 0) {
            return (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <EmptyState 
                        message="No sets in your price list yet. Search for sets above and add them to get started."
                        icon={faDollarSign}
                        className="py-12"
                    />
                </div>
            );
        }
        
        const filteredList = getFilteredPriceList();
        
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {renderPriceListHeader()}
                
                {priceError && (
                    <div className="p-4 border-b border-red-100">
                        <ErrorMessage message={priceError} />
                    </div>
                )}
                
                <div className="p-4 md:p-6">
                    {isLoadingPrices ? (
                        <div className="py-16">
                            <LoadingSpinner text="Refreshing price data..." size="lg" />
                        </div>
                    ) : filteredList.length === 0 && showOnlyWithPrices ? (
                        <EmptyState 
                            message="No sets with price data found. Try unchecking 'Only show sets with prices' to see all sets."
                            icon={faExclamationTriangle}
                            className="py-12"
                        />
                    ) : activeView === 'grid' ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                            {filteredList.map(set => renderSetGridView(set))}
                        </div>
                    ) : (
                        <div className="space-y-2 md:space-y-3">
                            {filteredList.map(set => renderSetListView(set))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-3 md:px-4">
                {/* Page Header */}
                <div className="mb-6 bg-gradient-to-r from-blue-700 to-blue-800 rounded-xl shadow-md overflow-hidden">
                    <div className="py-6 md:py-8 px-5 md:px-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                            LEGO Price Tool
                        </h1>
                        <p className="text-blue-200 text-sm md:text-base max-w-xl">
                            Search for sets and compare retail, sealed, and used market prices side by side.
                        </p>
                    </div>
                </div>

                {/* Search Section */}
                {renderSearchSection()}

                {/* Ad between search and price list */}
                <AdSenseAd type="in-article" className="mb-6" />

                {/* Price List Section */}
                {renderPriceListSection()}
            </div>
        </div>
    );
};

export default PriceTool;
